import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import Sidebar from './Sidebar';
import Header from './Header';
import Modal from './Modal';
import type {
  FacilitySubmodule,
  LayoutOutletContext,
  PortalRole,
  UserProfile,
} from '../types/auth';

type MainLayoutProps = {
  onLogout: () => void;
  userRole: PortalRole;
  permissions: string[];
  userProfile: UserProfile | null;
  facilitySubmodules: FacilitySubmodule[];
};

const MainLayout = ({
  onLogout,
  userRole,
  permissions,
  userProfile,
  facilitySubmodules,
}: MainLayoutProps) => {
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  const outletContext: LayoutOutletContext = {
    userRole,
    permissions,
    userProfile,
  };

  return (
    <div className="min-h-[125vh] bg-[#f8fafc] text-slate-900">
      {isSidebarOpen && (
        <button
          className="fixed inset-0 z-40 bg-slate-950/40 backdrop-blur-sm lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
          aria-label="Tutup menu"
          type="button"
        />
      )}

      <aside
        className={`fixed left-0 top-0 z-50 h-[125vh] w-[280px] transform bg-white transition-transform duration-300 ease-out lg:translate-x-0 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <Sidebar 
          onClose={() => setIsSidebarOpen(false)}
          permissions={permissions}
          userRole={userRole}
          facilitySubmodules={facilitySubmodules}
        />
      </aside>

      <div className="min-h-[125vh] lg:pl-[280px]">
        <Header
          userProfile={userProfile}
          userRole={userRole}
          onMenuClick={() => setIsSidebarOpen(true)}
          onLogout={() => setShowLogoutConfirm(true)}
        />

        <main className="px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-[1540px]">
            <Outlet context={outletContext} />
          </div>
        </main>

        
      <Modal
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        title=""
        footer={null}
        size="sm"
        closeOnOverlayClick={true}
        showCloseButton={true}
        className="overflow-hidden"
        titleClassName="hidden"
        contentClassName="!p-0"
      >
        <div className="flex flex-col items-center text-center p-6 pt-8">
          <div className="relative mb-5">
            <div className="absolute inset-0 bg-red-100 rounded-full animate-ping opacity-75" />
            <div className="relative p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-full">
              <AlertTriangle size={36} className="text-red-500" />
            </div>
          </div>

          <h3 className="text-xl font-extrabold text-slate-800 tracking-tight mb-2">
            Konfirmasi Keluar
          </h3>
          <p className="text-sm text-slate-500 leading-relaxed max-w-xs">
            Apakah Anda yakin ingin keluar dari sistem{' '}
            <span className="font-semibold text-slate-700">
              Facility Management
            </span>
            ? Anda harus login kembali untuk mengakses data.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 px-6 pb-6 pt-2 border-t border-slate-200/80 bg-slate-50/50 rounded-b-2xl">
          <button
            className="w-full sm:flex-1 px-5 py-2.5 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-xl cursor-pointer transition-all duration-200 hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200"
            onClick={() => setShowLogoutConfirm(false)}
          >
            Batal
          </button>
          <button
            className="w-full sm:flex-1 px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 rounded-xl cursor-pointer transition-all duration-200 hover:from-red-600 hover:to-red-700 hover:shadow-lg hover:shadow-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            onClick={onLogout}
          >
            Ya, Keluar
          </button>
        </div>
      </Modal>
      </div>
    </div>
  );
};

export default MainLayout;
